/**
 * ReportsManager Component
 *
 * Manages reports with drag-and-drop reordering.
 */
import { useState, useCallback, useMemo } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { ClientFilter, SortableTable, ConfirmDialog, type Column } from '../ui';
import { ReportForm } from './ReportForm';
import type { Report, ReportFormData } from '../../types';

export const ReportsManager = () => {
  const {
    clients,
    isLoading,
    selectedClientId,
    setSelectedClientId,
    filteredReports,
    createReport,
    updateReport,
    deleteReport,
    reorderReports,
    getClientById,
  } = useAdmin();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sort reports by order
  const sortedReports = useMemo(() => {
    return [...filteredReports].sort((a, b) => a.order - b.order);
  }, [filteredReports]);

  // Open form for adding
  const handleAdd = () => {
    setSelectedReport(null);
    setIsFormOpen(true);
  };

  // Open form for editing
  const handleEdit = (report: Report) => {
    setSelectedReport(report);
    setIsFormOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (report: Report) => {
    setSelectedReport(report);
    setIsDeleteDialogOpen(true);
  };

  // Submit form (create or update)
  const handleFormSubmit = useCallback(
    async (data: ReportFormData) => {
      setIsSubmitting(true);
      try {
        if (selectedReport) {
          await updateReport(selectedReport.id, data);
        } else {
          await createReport(data);
        }
        setIsFormOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedReport, createReport, updateReport]
  );

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedReport) return;

    setIsSubmitting(true);
    try {
      await deleteReport(selectedReport.id);
      setIsDeleteDialogOpen(false);
      setSelectedReport(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedReport, deleteReport]);

  // Handle reorder
  const handleReorder = useCallback(
    async (orderedIds: string[]) => {
      if (!selectedClientId) return;
      await reorderReports(selectedClientId, orderedIds);
    },
    [selectedClientId, reorderReports]
  );

  // Table columns
  const columns: Column<Report>[] = [
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'iframeSrc',
      header: 'Power BI URL',
      render: (report) => (
        <div className="max-w-xs truncate text-xs text-gray-500 font-mono">
          {report.iframeSrc}
        </div>
      ),
    },
    {
      key: 'clientId',
      header: 'Client',
      render: (report) => {
        const client = getClientById(report.clientId);
        return (
          <div className="flex items-center gap-2">
            {client && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: client.color }}
              />
            )}
            <span>{client?.name || '-'}</span>
          </div>
        );
      },
    },
    {
      key: 'order',
      header: 'Order',
      width: '80px',
      render: (report) => (
        <span className="text-gray-600">{report.order}</span>
      ),
    },
  ];

  // Determine if we should show sortable table (when filtered by client)
  const showSortable = !!selectedClientId;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Reports</h3>
        <div className="flex items-center gap-4">
          <ClientFilter
            clients={clients}
            selectedClientId={selectedClientId}
            onChange={setSelectedClientId}
            loading={isLoading}
          />
          <button
            onClick={handleAdd}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green"
          >
            + Add Report
          </button>
        </div>
      </div>

      {/* Info message for drag-and-drop */}
      {showSortable && sortedReports.length > 1 && (
        <p className="text-sm text-gray-500 mb-2">
          Drag rows to reorder reports for this client.
        </p>
      )}

      {/* Table - Sortable when filtered by client */}
      {showSortable ? (
        <SortableTable
          columns={columns}
          data={sortedReports}
          loading={isLoading}
          keyExtractor={(r) => r.id}
          emptyMessage="No reports for this client."
          onReorder={handleReorder}
          actions={(report) => (
            <>
              <button
                onClick={() => handleEdit(report)}
                className="p-1 text-gray-400 hover:text-metrica-green transition-colors"
                title="Edit"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => handleDeleteClick(report)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        />
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-2">
            Select a client to enable drag-and-drop reordering.
          </p>
          <SortableTable
            columns={columns}
            data={sortedReports}
            loading={isLoading}
            keyExtractor={(r) => r.id}
            emptyMessage="No reports yet. Add your first report to get started."
            onReorder={() => {}} // No-op when not filtered
            actions={(report) => (
              <>
                <button
                  onClick={() => handleEdit(report)}
                  className="p-1 text-gray-400 hover:text-metrica-green transition-colors"
                  title="Edit"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteClick(report)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </>
            )}
          />
        </>
      )}

      {/* Add/Edit Form */}
      <ReportForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        report={selectedReport}
        clients={clients}
        selectedClientId={selectedClientId}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        message={`Are you sure you want to delete "${selectedReport?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default ReportsManager;
